<template>
  <base-modal
    ref="modalRef"
    :nested="false"
    size="md"
  >
    <template #header>
      <h4>{{ $t('components.assets.defaultLocation.title') }}</h4>
    </template>

    <template #body>
      <div class="asset-default-location-form">
        <!-- Alert for errors -->
        <div
          v-if="errorMessage"
          class="alert alert-danger d-flex align-items-center gap-2 mb-4"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ errorMessage }}</span>
        </div>

        <!-- Asset Info -->
        <div class="asset-info mb-4">
          <h6 class="text-muted">
            {{ $t('components.assets.defaultLocation.assetInfo') }}
          </h6>
          <div class="fw-bold">
            {{ assetName }}
          </div>
          <div class="text-muted small">
            {{ $t('components.assets.dataGrids.serialNumber') }}: {{ assetSerialNumber }}
          </div>
        </div>

        <form @submit.prevent="handleSubmit">
          <!-- Address Search Section -->
          <div class="form-section mb-4">
            <h6 class="form-section-title mb-3">
              <i class="bi bi-search me-2" />
              {{ $t('components.assets.defaultLocation.searchByAddress') }}
            </h6>

            <div class="row">
              <!-- Street -->
              <div class="col-md-12 mb-3">
                <label
                  for="street"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.street') }}
                </label>
                <input
                  id="street"
                  v-model="address.street"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': addressValidationErrors.street }"
                  :placeholder="$t('components.assetGroups.modal.placeholders.street')"
                >
                <div
                  v-if="addressValidationErrors.street"
                  class="invalid-feedback"
                >
                  {{ addressValidationErrors.street }}
                </div>
              </div>
            </div>

            <div class="row">
              <!-- City -->
              <div class="col-md-6 mb-3">
                <label
                  for="city"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.city') }}
                </label>
                <input
                  id="city"
                  v-model="address.city"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': addressValidationErrors.city }"
                  :placeholder="$t('components.assetGroups.modal.placeholders.city')"
                >
                <div
                  v-if="addressValidationErrors.city"
                  class="invalid-feedback"
                >
                  {{ addressValidationErrors.city }}
                </div>
              </div>

              <!-- Postal Code -->
              <div class="col-md-6 mb-3">
                <label
                  for="postalCode"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.postalCode') }}
                </label>
                <input
                  id="postalCode"
                  v-model="address.postalCode"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': addressValidationErrors.postalCode }"
                  :placeholder="$t('components.assetGroups.modal.placeholders.postalCode')"
                >
                <div
                  v-if="addressValidationErrors.postalCode"
                  class="invalid-feedback"
                >
                  {{ addressValidationErrors.postalCode }}
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Country -->
              <div class="col-md-12 mb-3">
                <label
                  for="country"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.country') }}
                </label>
                <select
                  id="country"
                  v-model="address.country"
                  class="form-select"
                  :class="{ 'is-invalid': addressValidationErrors.country }"
                >
                  <option :value="null">
                    {{ $t('components.assetGroups.modal.selectCountry') }}
                  </option>
                  <option
                    v-for="(countryName, countryKey) in countries"
                    :key="countryKey"
                    :value="countryKey"
                  >
                    {{ countryName }}
                  </option>
                </select>
                <div
                  v-if="addressValidationErrors.country"
                  class="invalid-feedback"
                >
                  {{ addressValidationErrors.country }}
                </div>
              </div>
            </div>

            <!-- Geocode Button -->
            <div class="row">
              <div class="col-md-12 mb-3">
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="loadingGeocode || !address.street || !address.city || !address.postalCode || !address.country"
                  @click="handleGeocode"
                >
                  <span
                    v-if="loadingGeocode"
                    class="spinner-border spinner-border-sm me-2"
                  />
                  <i
                    v-else
                    class="bi bi-geo-alt-fill me-2"
                  />
                  {{ $t('components.assetGroups.modal.geocodeButton') }}
                </button>
                <small
                  v-if="!geocodeError"
                  class="text-muted d-block mt-1"
                >
                  {{ $t('components.assets.defaultLocation.geocodeHint') }}
                </small>
                <div
                  v-if="geocodeError"
                  class="alert alert-danger alert-sm mt-2 mb-0"
                >
                  <i class="bi bi-exclamation-triangle-fill me-2" />
                  <span>{{ geocodeError }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Coordinates Section -->
          <div class="form-section">
            <h6 class="form-section-title mb-3">
              <i class="bi bi-geo-alt me-2" />
              {{ $t('components.assets.defaultLocation.coordinates') }}
            </h6>

            <div class="row">
              <!-- Latitude -->
              <div class="col-md-6 mb-3">
                <label
                  for="lat"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.latitude') }}
                </label>
                <input
                  id="lat"
                  v-model.number="form.defaultLatitude"
                  type="number"
                  step="0.00000001"
                  class="form-control"
                  :class="{ 'is-invalid': coordinatesValidationErrors.latitude }"
                  :placeholder="$t('components.assetGroups.modal.placeholders.latitude')"
                  min="-90"
                  max="90"
                  @blur="validateCoordinates"
                >
                <div
                  v-if="coordinatesValidationErrors.latitude"
                  class="invalid-feedback"
                >
                  {{ coordinatesValidationErrors.latitude }}
                </div>
              </div>

              <!-- Longitude -->
              <div class="col-md-6 mb-3">
                <label
                  for="lng"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.longitude') }}
                </label>
                <input
                  id="lng"
                  v-model.number="form.defaultLongitude"
                  type="number"
                  step="0.00000001"
                  class="form-control"
                  :class="{ 'is-invalid': coordinatesValidationErrors.longitude }"
                  :placeholder="$t('components.assetGroups.modal.placeholders.longitude')"
                  min="-180"
                  max="180"
                  @blur="validateCoordinates"
                >
                <div
                  v-if="coordinatesValidationErrors.longitude"
                  class="invalid-feedback"
                >
                  {{ coordinatesValidationErrors.longitude }}
                </div>
              </div>
            </div>

            <!-- Google Maps Link -->
            <div
              v-if="hasValidCoordinates"
              class="row mt-3"
            >
              <div class="col-md-12">
                <a
                  :href="googleMapsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-outline-success btn-sm"
                >
                  <i class="bi bi-geo-alt-fill me-2" />
                  {{ $t('components.assets.defaultLocation.checkOnMap') }}
                  <i class="bi bi-box-arrow-up-right ms-2" />
                </a>
              </div>
            </div>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <button
        type="button"
        class="btn btn-secondary"
        @click="closeModal"
      >
        {{ $t('components.assetGroups.modal.cancel') }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="loading"
        @click="handleSubmit"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-2"
        />
        {{ $t('components.assetGroups.modal.update') }}
      </button>
    </template>
  </base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from './core/BaseModal.vue'
import { getGeocode, getReverseGeocodeDetailed } from '@/crud/getters'
import { updateAssetDefaultLocation } from '@/crud/asset'

type AssetDefaultLocationForm = {
  defaultLatitude: number | null
  defaultLongitude: number | null
}

export default defineComponent({
  name: 'AssetDefaultLocationModal',
  components: { BaseModal },
  emits: ['saved'],
  setup(props, { emit }) {
    const { t, tm } = useI18n()
    const modalRef = ref<InstanceType<typeof BaseModal> | null>(null)
    const assetId = ref<number | null>(null)
    const assetName = ref<string>('')
    const assetSerialNumber = ref<string>('')
    const loading = ref(false)
    const loadingGeocode = ref(false)
    const geocodeError = ref<string | null>(null)
    const errorMessage = ref<string | null>(null)

    const form = reactive<AssetDefaultLocationForm>({
      defaultLatitude: null,
      defaultLongitude: null
    })

    const address = reactive({
      street: null as string | null,
      city: null as string | null,
      postalCode: null as string | null,
      country: 'poland' as string | null
    })

    const coordinatesValidationErrors = reactive({
      latitude: null as string | null,
      longitude: null as string | null
    })

    const addressValidationErrors = reactive({
      street: null as string | null,
      city: null as string | null,
      postalCode: null as string | null,
      country: null as string | null
    })

    const countries = computed<Record<string, string>>(() => {
      const countryMessages = tm('components.history.summary.countries')
      if (countryMessages && typeof countryMessages === 'object' && !Array.isArray(countryMessages)) {
        return countryMessages as Record<string, string>
      }
      return {}
    })

    const hasValidCoordinates = computed(() => {
      return form.defaultLatitude !== null &&
             form.defaultLatitude !== undefined &&
             form.defaultLongitude !== null &&
             form.defaultLongitude !== undefined &&
             typeof form.defaultLatitude === 'number' &&
             typeof form.defaultLongitude === 'number' &&
             !isNaN(form.defaultLatitude) &&
             !isNaN(form.defaultLongitude)
    })

    const googleMapsUrl = computed(() => {
      if (!hasValidCoordinates.value) return ''
      return `https://www.google.com/maps?q=${form.defaultLatitude},${form.defaultLongitude}&z=18`
    })

    const resetForm = () => {
      form.defaultLatitude = null
      form.defaultLongitude = null
      address.street = null
      address.city = null
      address.postalCode = null
      address.country = 'poland'
      geocodeError.value = null
      errorMessage.value = null
      assetId.value = null
      assetName.value = ''
      assetSerialNumber.value = ''
      resetValidationErrors()
    }

    const resetValidationErrors = () => {
      coordinatesValidationErrors.latitude = null
      coordinatesValidationErrors.longitude = null
      addressValidationErrors.street = null
      addressValidationErrors.city = null
      addressValidationErrors.postalCode = null
      addressValidationErrors.country = null
    }

    // Map ISO country codes to country keys used in select
    const mapCountryCodeToKey = (countryCode: string | null): string | null => {
      if (!countryCode) return null

      const codeMap: Record<string, string> = {
        'pl': 'poland',
        'de': 'germany',
        'us': 'united states',
        'gb': 'united kingdom',
        'fr': 'france',
        'es': 'spain',
        'it': 'italy',
        'nl': 'netherlands',
        'be': 'belgium',
        'at': 'austria',
        'ch': 'switzerland',
        'cz': 'czech republic',
        'sk': 'slovakia',
        'hu': 'hungary',
        'ro': 'romania',
        'bg': 'bulgaria',
        'hr': 'croatia',
        'si': 'slovenia',
        'lt': 'lithuania',
        'lv': 'latvia',
        'ee': 'estonia',
        'se': 'sweden',
        'no': 'norway',
        'fi': 'finland',
        'dk': 'denmark',
        'ie': 'ireland',
        'pt': 'portugal',
        'gr': 'greece',
        'rs': 'serbia',
        'ua': 'ukraine',
        'ru': 'russia',
        'by': 'belarus',
        'tr': 'turkey'
      }

      return codeMap[countryCode.toLowerCase()] || null
    }

    const validateCoordinates = () => {
      coordinatesValidationErrors.latitude = null
      coordinatesValidationErrors.longitude = null

      // Check if latitude is required
      if (form.defaultLatitude === null || form.defaultLatitude === undefined) {
        coordinatesValidationErrors.latitude = t('components.assets.defaultLocation.validation.latitudeRequired')
      } else if (typeof form.defaultLatitude !== 'number' || isNaN(form.defaultLatitude)) {
        coordinatesValidationErrors.latitude = t('components.assets.defaultLocation.validation.latitudeInvalid')
      } else if (form.defaultLatitude < -90 || form.defaultLatitude > 90) {
        coordinatesValidationErrors.latitude = t('components.assets.defaultLocation.validation.latitudeRange')
      }

      // Check if longitude is required
      if (form.defaultLongitude === null || form.defaultLongitude === undefined) {
        coordinatesValidationErrors.longitude = t('components.assets.defaultLocation.validation.longitudeRequired')
      } else if (typeof form.defaultLongitude !== 'number' || isNaN(form.defaultLongitude)) {
        coordinatesValidationErrors.longitude = t('components.assets.defaultLocation.validation.longitudeInvalid')
      } else if (form.defaultLongitude < -180 || form.defaultLongitude > 180) {
        coordinatesValidationErrors.longitude = t('components.assets.defaultLocation.validation.longitudeRange')
      }

      return !coordinatesValidationErrors.latitude && !coordinatesValidationErrors.longitude
    }

    const validateAddress = () => {
      addressValidationErrors.street = null
      addressValidationErrors.city = null
      addressValidationErrors.postalCode = null
      addressValidationErrors.country = null

      const hasAnyAddressField = address.street || address.city || address.postalCode || address.country

      if (hasAnyAddressField) {
        if (!address.street || address.street.trim() === '') {
          addressValidationErrors.street = t('components.assets.defaultLocation.validation.streetRequired')
        }
        if (!address.city || address.city.trim() === '') {
          addressValidationErrors.city = t('components.assets.defaultLocation.validation.cityRequired')
        }
        if (!address.postalCode || address.postalCode.trim() === '') {
          addressValidationErrors.postalCode = t('components.assets.defaultLocation.validation.postalCodeRequired')
        }
        if (!address.country) {
          addressValidationErrors.country = t('components.assets.defaultLocation.validation.countryRequired')
        }
      }

      return !addressValidationErrors.street &&
             !addressValidationErrors.city &&
             !addressValidationErrors.postalCode &&
             !addressValidationErrors.country
    }

    const openModal = async (asset: any) => {
      resetForm()

      assetId.value = asset.id
      assetName.value = asset.name || asset.serial_number || `Asset #${asset.id}`
      assetSerialNumber.value = asset.serial_number || '—'

      // Load existing default location if available
      const lat = asset.default_latitude || asset.defaultLatitude
      const lng = asset.default_longitude || asset.defaultLongitude

      if (lat && lng) {
        form.defaultLatitude = parseFloat(lat)
        form.defaultLongitude = parseFloat(lng)

        // Perform reverse geocoding to get address
        try {
          const coords = `${lat},${lng}`
          const response = await getReverseGeocodeDetailed(coords)
          if (response.data && response.data.address) {
            // Parse address from Nominatim format
            const addr = response.data.address
            // Combine street with house number if available
            const streetName = addr.road || addr.street || ''
            const houseNumber = addr.house_number || ''
            address.street = houseNumber && streetName
              ? `${streetName} ${houseNumber}`
              : (streetName || null)
            address.city = addr.city || addr.town || addr.village || null
            address.postalCode = addr.postcode || null
            address.country = mapCountryCodeToKey(addr.country_code)
          }
        } catch (e) {
          console.error('Reverse geocoding error:', e)
          // Continue without address data
        }
      }

      modalRef.value?.openModal()
    }

    const closeModal = () => {
      modalRef.value?.closeModal()
      resetForm()
    }

    const handleGeocode = async () => {
      // Validate address before geocoding
      if (!validateAddress()) {
        return
      }

      const addressParts: string[] = []
      if (address.street) addressParts.push(address.street)
      if (address.postalCode) addressParts.push(address.postalCode)
      if (address.city) addressParts.push(address.city)
      if (address.country) {
        const countryName = countries.value[address.country]
        if (countryName) addressParts.push(countryName)
      }

      const fullAddress = addressParts.join(', ')

      if (!fullAddress) {
        geocodeError.value = t('components.assetGroups.modal.errors.noAddressForGeocode')
        return
      }

      loadingGeocode.value = true
      geocodeError.value = null

      try {
        const response = await getGeocode(fullAddress)
        if (response.data && response.data.lat && response.data.lng) {
          form.defaultLatitude = parseFloat(response.data.lat)
          form.defaultLongitude = parseFloat(response.data.lng)
          geocodeError.value = null
          // Clear validation errors after successful geocoding
          resetValidationErrors()
        } else {
          geocodeError.value = t('components.assetGroups.modal.errors.geocodeFailed')
        }
      } catch (e: any) {
        console.error('Geocoding error:', e)
        if (e?.response?.status === 404) {
          geocodeError.value = t('components.assetGroups.modal.errors.geocodeFailed')
        } else {
          const apiError = e?.response?.data?.error
          geocodeError.value = apiError || t('components.assetGroups.modal.errors.geocodeFailed')
        }
      } finally {
        loadingGeocode.value = false
      }
    }

    const handleSubmit = async () => {
      if (!assetId.value) {
        errorMessage.value = t('components.assets.defaultLocation.errors.noAssetId')
        return
      }

      // Validate coordinates
      const isCoordinatesValid = validateCoordinates()

      if (!isCoordinatesValid) {
        return
      }

      loading.value = true
      errorMessage.value = null

      try {
        const payload = {
          defaultLatitude: form.defaultLatitude,
          defaultLongitude: form.defaultLongitude
        }

        const response = await updateAssetDefaultLocation(assetId.value, payload)

        // Update address fields from reverse geocoding
        try {
          const coords = `${form.defaultLatitude},${form.defaultLongitude}`
          const geoResponse = await getReverseGeocodeDetailed(coords)
          if (geoResponse.data && geoResponse.data.address) {
            const addr = geoResponse.data.address
            // Combine street with house number if available
            const streetName = addr.road || addr.street || ''
            const houseNumber = addr.house_number || ''
            address.street = houseNumber && streetName
              ? `${streetName} ${houseNumber}`
              : (streetName || null)
            address.city = addr.city || addr.town || addr.village || null
            address.postalCode = addr.postcode || null
            address.country = mapCountryCodeToKey(addr.country_code)
          }
        } catch (e) {
          console.error('Reverse geocoding error:', e)
        }

        emit('saved', response.data)
        closeModal()
      } catch (e: any) {
        errorMessage.value = e?.response?.data?.error || e?.message || t('components.assets.defaultLocation.errors.updateFailed')
      } finally {
        loading.value = false
      }
    }

    // Clear geocode error when user changes address fields
    watch([() => address.street, () => address.city, () => address.postalCode, () => address.country], () => {
      if (geocodeError.value) {
        geocodeError.value = null
      }
    })

    return {
      modalRef,
      assetId,
      assetName,
      assetSerialNumber,
      loading,
      loadingGeocode,
      geocodeError,
      errorMessage,
      form,
      address,
      countries,
      coordinatesValidationErrors,
      addressValidationErrors,
      hasValidCoordinates,
      googleMapsUrl,
      openModal,
      closeModal,
      handleGeocode,
      handleSubmit,
      validateCoordinates,
      validateAddress
    }
  }
})
</script>

<style scoped lang="scss">
.asset-default-location-form {
  .asset-info {
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
  }

  .form-section {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 16px;

    i {
      color: #252422;
    }
  }

  .alert-sm {
    padding: 8px 12px;
    font-size: 13px;
  }
}
</style>
